(() => {
  if (customElements.get("collection-load-more")) {
    return;
  }

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      this.isLoading = false;
      this.handleClick = this.handleClick || this.onClick.bind(this);
      this.addEventListener("click", this.handleClick);
      this.observe();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.observer?.disconnect();
      this.abortController?.abort();
    }

    observe() {
      this.observer?.disconnect();

      if (!("IntersectionObserver" in window)) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.load();
          }
        },
        { rootMargin: "300px 0px" }
      );
      this.observer.observe(this);
    }

    onClick(event) {
      const link = event.target.closest("a");

      if (!link || !this.contains(link)) {
        return;
      }

      event.preventDefault();
      this.load();
    }

    async load() {
      const link = this.querySelector('a[rel="next"]');

      if (this.isLoading || !link) {
        return;
      }

      const sectionId = this.dataset.sectionId;
      const currentSection = this.closest(".shopify-section--main-collection");
      const currentGrid = currentSection?.querySelector("[data-collection-product-grid]");

      if (!sectionId || !currentGrid) {
        return;
      }

      this.isLoading = true;
      this.observer?.disconnect();
      this.abortController = new AbortController();

      const originalLinkContent = link.innerHTML;
      link.textContent = this.dataset.loadingText || "";
      link.setAttribute("aria-busy", "true");

      try {
        const response = await fetch(link.href, {
          cache: "no-store",
          credentials: "same-origin",
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Collection page request failed with ${response.status}`);
        }

        const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
        const responseSection = responseDocument.getElementById(`shopify-section-${sectionId}`);
        const responseGrid = responseSection?.querySelector("[data-collection-product-grid]");
        const responseLoader = responseSection?.querySelector("collection-load-more");

        if (!responseGrid) {
          throw new Error("The collection response did not contain the expected product grid");
        }

        const fragment = document.createDocumentFragment();
        Array.from(responseGrid.children).forEach((item) => fragment.append(item));
        currentGrid.append(fragment);

        document.dispatchEvent(
          new CustomEvent("ajaxinate:loaded", {
            detail: { url: link.href, sectionId }
          })
        );

        ["swiperAnnouncement", "cardsSwiper", "cardSwiperTeaser"].forEach((functionName) => {
          if (typeof window[functionName] === "function") {
            window[functionName]();
          }
        });

        if (responseLoader) {
          this.replaceWith(document.importNode(responseLoader, true));
        } else {
          this.remove();
        }
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        link.innerHTML = originalLinkContent;
        link.removeAttribute("aria-busy");
        this.isLoading = false;
        console.error("Collection pagination failed:", error);
      }
    }
  }

  customElements.define("collection-load-more", CollectionLoadMore);
})();
